import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { AlertCircle, CheckCircle, XCircle, Lightbulb } from 'lucide-react'
import { Link } from 'react-router-dom'

export default function DecisionPoint({
  situation,
  question,
  options,
  correctOption,
  outcomes,
  consequences,
  lessonHint,
  onChoiceSelect
}) {
  const [selectedChoice, setSelectedChoice] = useState(null)
  const [showOutcome, setShowOutcome] = useState(false)

  const handleChoice = (index) => {
    setSelectedChoice(index)
    setShowOutcome(true)

    // Call parent callback with choice and consequences
    if (onChoiceSelect) {
      const consequence = consequences ? JSON.parse(consequences)[index] : {}
      onChoiceSelect(index, consequence)
    }
  }

  const getOutcomeType = (index) => {
    const outcomeData = outcomes ? JSON.parse(outcomes)[index] : {}
    return outcomeData.type || 'neutral'
  }

  const getOutcomeText = (index) => {
    const outcomeData = outcomes ? JSON.parse(outcomes)[index] : {}
    return outcomeData.text || 'You made your choice.'
  }

  const outcomeTypeStyles = {
    excellent: {
      bg: 'bg-green-50',
      border: 'border-green-500',
      text: 'text-green-800',
      icon: CheckCircle,
      iconColor: 'text-green-600'
    },
    good: {
      bg: 'bg-blue-50',
      border: 'border-blue-500',
      text: 'text-blue-800',
      icon: CheckCircle,
      iconColor: 'text-blue-600'
    },
    safe: {
      bg: 'bg-gray-50',
      border: 'border-gray-500',
      text: 'text-gray-800',
      icon: AlertCircle,
      iconColor: 'text-gray-600'
    },
    risky: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-500',
      text: 'text-yellow-800',
      icon: AlertCircle,
      iconColor: 'text-yellow-600'
    },
    bad: {
      bg: 'bg-orange-50',
      border: 'border-orange-500',
      text: 'text-orange-800',
      icon: XCircle,
      iconColor: 'text-orange-600'
    },
    critical: {
      bg: 'bg-red-50',
      border: 'border-red-500',
      text: 'text-red-800',
      icon: XCircle,
      iconColor: 'text-red-600'
    }
  }

  const parsedOptions = typeof options === 'string' ? JSON.parse(options) : options

  return (
    <div className="max-w-3xl mx-auto">
      {/* Situation Description */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-900 text-white p-6 rounded-xl mb-6"
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold text-lg mb-2">Situation</h3>
            <p className="text-gray-200 leading-relaxed">{situation}</p>
          </div>
        </div>
      </motion.div>

      {/* Question */}
      <motion.h2
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-2xl font-bold text-gray-900 mb-6 text-center"
      >
        {question}
      </motion.h2>

      {/* Options */}
      {!showOutcome && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="grid gap-4 mb-6"
        >
          {parsedOptions.map((option, index) => (
            <motion.button
              key={index}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleChoice(index)}
              className="flex items-center gap-4 p-4 bg-white border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all text-left group"
            >
              <div className="text-4xl flex-shrink-0 group-hover:scale-110 transition-transform">
                {option.icon}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900 group-hover:text-green-700">
                  {option.text}
                </p>
              </div>
            </motion.button>
          ))}
        </motion.div>
      )}

      {/* Lesson Hint */}
      {!showOutcome && lessonHint && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3"
        >
          <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <p className="text-sm text-blue-800">
            Need help deciding?{' '}
            <Link to={`/lessons/${lessonHint}`} className="font-semibold underline hover:text-blue-600">
              Review the relevant lesson
            </Link>
          </p>
        </motion.div>
      )}

      {/* Outcome Display */}
      <AnimatePresence>
        {showOutcome && selectedChoice !== null && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            {/* Selected Choice Echo */}
            <div className="bg-gray-100 border border-gray-300 rounded-xl p-4 flex items-center gap-4">
              <div className="text-3xl">{parsedOptions[selectedChoice].icon}</div>
              <div>
                <p className="text-sm text-gray-600 mb-1">You chose:</p>
                <p className="font-semibold text-gray-900">{parsedOptions[selectedChoice].text}</p>
              </div>
            </div>

            {/* Outcome */}
            {(() => {
              const outcomeType = getOutcomeType(selectedChoice)
              const styles = outcomeTypeStyles[outcomeType] || outcomeTypeStyles.safe
              const OutcomeIcon = styles.icon

              return (
                <motion.div
                  initial={{ scale: 0.95 }}
                  animate={{ scale: 1 }}
                  className={`${styles.bg} border-2 ${styles.border} rounded-xl p-6`}
                >
                  <div className="flex items-start gap-3 mb-4">
                    <OutcomeIcon className={`w-6 h-6 ${styles.iconColor} flex-shrink-0 mt-1`} />
                    <div>
                      <h3 className={`font-bold text-lg mb-2 ${styles.text}`}>
                        {outcomeType === 'excellent' && 'Excellent Choice!'}
                        {outcomeType === 'good' && 'Good Decision'}
                        {outcomeType === 'safe' && 'Safe Choice'}
                        {outcomeType === 'risky' && 'Risky Decision'}
                        {outcomeType === 'bad' && 'Poor Choice'}
                        {outcomeType === 'critical' && 'Critical Mistake'}
                      </h3>
                      <p className={`${styles.text} leading-relaxed`}>
                        {getOutcomeText(selectedChoice)}
                      </p>
                    </div>
                  </div>

                  {/* Consequences */}
                  {consequences && (
                    <div className="flex gap-4 text-sm font-semibold">
                      {JSON.parse(consequences)[selectedChoice].xp !== undefined && (
                        <div className={JSON.parse(consequences)[selectedChoice].xp >= 0 ? 'text-green-700' : 'text-red-700'}>
                          XP: {JSON.parse(consequences)[selectedChoice].xp >= 0 ? '+' : ''}{JSON.parse(consequences)[selectedChoice].xp}
                        </div>
                      )}
                      {JSON.parse(consequences)[selectedChoice].health !== undefined && (
                        <div className={JSON.parse(consequences)[selectedChoice].health >= 0 ? 'text-green-700' : 'text-red-700'}>
                          Health: {JSON.parse(consequences)[selectedChoice].health >= 0 ? '+' : ''}{JSON.parse(consequences)[selectedChoice].health}
                        </div>
                      )}
                      {JSON.parse(consequences)[selectedChoice].score !== undefined && (
                        <div className="text-purple-700">
                          Score: {JSON.parse(consequences)[selectedChoice].score}
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              )
            })()}

            {/* Lesson Link */}
            {lessonHint && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
                <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <p className="text-sm text-blue-800">
                  Want to learn more?{' '}
                  <Link to={`/lessons/${lessonHint}`} className="font-semibold underline hover:text-blue-600">
                    Study the lesson on this topic
                  </Link>
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
